
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertCircle,
  BookOpen,
  CalendarDays,
  Download,
  FileText,
  IndianRupee,
  Info,
  Loader2,
  Printer,
  RefreshCw,
  School,
} from "lucide-react";

import { api } from "../lib/api";

/* ==========================================
   HELPERS
========================================== */

function money(value) {
  return `â‚¹${Number(value || 0).toLocaleString("en-IN")}`;
}

function escapeCSV(value) {
  let text = String(value ?? "");

  if (/^\s*[=+\-@]/.test(text)) {
    text = `'${text}`;
  }

  return `"${text.replace(/"/g, '""')}"`;
}

/* ==========================================
   MAIN COMPONENT
========================================== */

export default function FeeStructure() {
  const [feeStructure, setFeeStructure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;

    async function loadFeeStructure() {
      setLoading(true);
      setError("");

      try {
        const response = await api.activeFeeStructure();

        if (active) {
          setFeeStructure(response.data || null);
        }
      } catch (requestError) {
        if (active) {
          setError(
            requestError?.message ||
              "Unable to load fee structure."
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadFeeStructure();

    return () => {
      active = false;
    };
  }, [refreshKey]);

  function handleDownload() {
    if (!feeStructure?.rows?.length) return;

    const headers = [
      "Class",
      "Admission Fee",
      "Tuition Fee",
      "Yearly Development",
      "Examination Fee",
    ];

    const rows = feeStructure.rows.map((item) => [
      item.className,
      item.admissionFee,
      item.tuitionFee,
      item.yearlyDevelopment,
      item.examinationFee,
    ]);

    const csv = [headers, ...rows]
      .map((row) => row.map(escapeCSV).join(","))
      .join("\r\n");

    const blob = new Blob(["\uFEFF", csv], {
      type: "text/csv;charset=utf-8",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = `fee-structure-${
      feeStructure.academicYear || "school"
    }.csv`;

    document.body.appendChild(link);
    link.click();
    link.remove();

    window.setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 1000);
  }

  const feeRows = feeStructure?.rows || [];
  const notes = feeStructure?.notes || [];
  const paymentSchedule = feeStructure?.paymentSchedule || [];

  return (
    <main className="fee-public-page relative min-h-screen overflow-hidden bg-[#FFFCF9] pb-14 text-slate-800">

      {/* BACKGROUND DETAILS */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden print:hidden">
        <div className="absolute -left-32 top-52 h-96 w-96 rounded-full bg-red-100/50 blur-3xl" />
        <div className="absolute -right-32 bottom-10 h-96 w-96 rounded-full bg-amber-100/60 blur-3xl" />
      </div>

      {/* HERO SECTION */}
      <section className="fee-hero relative overflow-hidden bg-gradient-to-br from-[#671414] via-[#991b1b] to-[#b42727] px-4 pb-28 pt-14 text-white sm:px-6 lg:px-8">

        <div className="pointer-events-none absolute -right-24 -top-28 h-80 w-80 rounded-full border-[45px] border-white/[0.055]" />

        <div className="pointer-events-none absolute bottom-0 left-1/3 h-48 w-48 rounded-full bg-amber-300/10 blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative mx-auto max-w-7xl text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-200/30 bg-white/10 px-5 py-2 text-xs font-normal tracking-wide text-amber-100">
            <IndianRupee size={16} />
            School Fee Information
          </span>

          <h1 className="mt-5 font-serif text-[32px] font-medium leading-tight sm:text-[42px] lg:text-[52px]">
            Fee <span className="text-amber-300">Structure</span>
          </h1>

          <div className="mx-auto mt-4 h-1 w-24 rounded-full bg-amber-400" />

          <p className="mx-auto mt-5 max-w-2xl text-sm leading-7 text-white/85 sm:text-[15px]">
            Clear and accessible information about school fees,
            academic-year charges and payment schedules.
          </p>
        </motion.div>
      </section>

      {/* MAIN CONTENT */}
      <div className="fee-print-area relative z-10 mx-auto -mt-14 max-w-7xl px-4 sm:px-6 lg:px-8">

        {loading ? (
          <div className="flex min-h-[350px] flex-col items-center justify-center rounded-[24px] border border-red-100 bg-white shadow-lg">
            <Loader2
              size={34}
              className="animate-spin text-red-800"
            />
            <p className="mt-4 text-sm text-slate-600">
              Loading fee structure...
            </p>
          </div>
        ) : error || !feeStructure ? (
          <div className="mx-auto flex min-h-[300px] max-w-2xl flex-col items-center justify-center rounded-[24px] border border-red-100 bg-white px-6 py-10 text-center shadow-lg">

            <AlertCircle
              size={38}
              className="text-red-600"
            />

            <h2 className="mt-4 font-serif text-2xl font-medium text-slate-900">
              Fee Structure
            </h2>

            <p className="mt-3 text-sm leading-6 text-slate-500">
              {error ||
                "Fee structure has not been published yet."}
            </p>

            {error && (
              <button
                type="button"
                onClick={() =>
                  setRefreshKey((value) => value + 1)
                }
                className="mt-5 inline-flex items-center gap-2 rounded-xl bg-red-800 px-5 py-2.5 text-sm text-white transition hover:bg-red-900"
              >
                <RefreshCw size={16} />
                Try Again
              </button>
            )}
          </div>
        ) : (
          <>
            {/* ACADEMIC YEAR INFORMATION */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="fee-card overflow-hidden rounded-[24px] border border-red-100 bg-white shadow-[0_18px_50px_rgba(80,25,25,0.1)]"
            >
              <div className="flex flex-col justify-between gap-5 p-5 sm:flex-row sm:items-center sm:p-7">

                <div className="flex items-start gap-4">

                  <div className="flex h-13 w-13 shrink-0 items-center justify-center rounded-2xl bg-red-50 p-3 text-red-800">
                    <School size={25} />
                  </div>

                  <div>
                    <p className="text-[11px] uppercase tracking-[0.14em] text-amber-700">
                      Academic Fee Details
                    </p>

                    <h2 className="mt-1 font-serif text-xl font-medium text-slate-950 sm:text-[25px]">
                      {feeStructure.title || "Fee Structure of School"}
                    </h2>

                    <p className="mt-2 max-w-xl text-[13px] leading-6 text-slate-500">
                      {feeStructure.description ||
                        "All fees are in Indian Rupees (â‚¹)."}
                    </p>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-3 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-white px-5 py-3.5">

                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                    <CalendarDays size={20} />
                  </div>

                  <div>
                    <p className="text-xs text-slate-500">
                      Academic Year
                    </p>

                    <p className="mt-1 font-serif text-lg font-medium text-red-800">
                      {feeStructure.academicYear || "â€”"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="h-[3px] bg-gradient-to-r from-red-800 via-red-500 to-amber-400" />
            </motion.div>

            {/* ACTION BAR */}
            <div className="fee-actions my-6 flex flex-wrap items-center justify-between gap-4">

              <div className="flex items-center gap-2 text-[13px] text-slate-600">
                <BookOpen size={18} className="text-red-700" />
                Class-wise Fee Information
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleDownload}
                  disabled={!feeRows.length}
                  className="inline-flex items-center gap-2 rounded-xl bg-red-800 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-red-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Download size={16} />
                  Download CSV
                </button>

                <button
                  type="button"
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-5 py-2.5 text-sm font-medium text-red-800 transition hover:bg-red-50"
                >
                  <Printer size={16} />
                  Print
                </button>
              </div>
            </div>

            {/* FEE TABLE */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="fee-card overflow-hidden rounded-[24px] border border-red-100 bg-white shadow-[0_12px_40px_rgba(80,25,25,0.07)]"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-red-100 bg-gradient-to-r from-[#fff6f4] via-white to-[#fffaf0] px-5 py-5 sm:px-7">

                <div className="flex items-center gap-3">

                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-red-800 text-white">
                    <BookOpen size={21} />
                  </div>

                  <div>
                    <h3 className="font-serif text-xl font-medium text-slate-950">
                      Class-wise Fee Details
                    </h3>

                    <p className="mt-1 text-xs text-slate-500">
                      Fee details for the selected academic year
                    </p>
                  </div>
                </div>

                <span className="rounded-full border border-amber-200 bg-amber-50 px-4 py-1.5 text-xs text-amber-800">
                  {feeRows.length}{" "}
                  {feeRows.length === 1 ? "Class" : "Classes"}
                </span>
              </div>

              <div className="overflow-x-auto">

                <table className="fee-table w-full min-w-[880px] border-collapse text-sm">

                  <thead>
                    <tr className="bg-gradient-to-r from-red-900 to-red-800 text-white">
                      <th className="px-5 py-4 text-left font-medium">
                        Class
                      </th>

                      <th className="px-5 py-4 text-center font-medium">
                        Admission Fee (â‚¹)
                      </th>

                      <th className="px-5 py-4 text-center font-medium">
                        Tuition Fee (â‚¹)
                      </th>

                      <th className="px-5 py-4 text-center font-medium">
                        Yearly Development (â‚¹)
                      </th>

                      <th className="px-5 py-4 text-center font-medium">
                        Examination Fee (â‚¹)
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">

                    {feeRows.length > 0 ? (
                      feeRows.map((item, index) => (
                        <tr
                          key={`${item.className}-${index}`}
                          className="transition-colors odd:bg-white even:bg-[#FFFAF6] hover:bg-red-50/50"
                        >
                          <td className="px-5 py-4 font-medium text-slate-900">
                            {item.className || "â€”"}
                          </td>

                          <td className="px-5 py-4 text-center text-slate-600">
                            {money(item.admissionFee)}
                          </td>

                          <td className="px-5 py-4 text-center text-slate-600">
                            {money(item.tuitionFee)}
                          </td>

                          <td className="px-5 py-4 text-center text-slate-600">
                            {money(item.yearlyDevelopment)}
                          </td>

                          <td className="px-5 py-4 text-center text-slate-600">
                            {money(item.examinationFee)}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-5 py-10 text-center text-slate-500"
                        >
                          No class fee details available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-start gap-2 border-t border-red-100 bg-red-50/40 px-5 py-3.5 text-xs text-slate-500 sm:px-7">
                <Info size={16} className="shrink-0 text-red-700" />
                All amounts are displayed in Indian Rupees (â‚¹).
              </div>
            </motion.div>

            {/* NOTES AND PAYMENT SCHEDULE */}
            {(notes.length > 0 ||
              paymentSchedule.length > 0) && (
              <div className="mt-7 grid items-start gap-6 lg:grid-cols-2">

                {/* IMPORTANT NOTES */}
                {notes.length > 0 && (
                  <div className="fee-card overflow-hidden rounded-[22px] border border-red-100 bg-white shadow-[0_10px_35px_rgba(80,25,25,0.055)]">

                    <div className="flex items-center gap-3 border-b border-red-100 bg-gradient-to-r from-red-50 to-white px-5 py-4">

                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-800">
                        <BookOpen size={19} />
                      </div>

                      <h3 className="font-serif text-lg font-medium text-slate-900">
                        Important Notes
                      </h3>
                    </div>

                    <div className="space-y-3 p-5 sm:p-6">
                      {notes.map((note, index) => (
                        <div
                          key={index}
                          className="flex items-start gap-3"
                        >
                          <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-700" />

                          <p className="text-[13px] leading-7 text-slate-600">
                            {note}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* PAYMENT SCHEDULE */}
                {paymentSchedule.length > 0 && (
                  <div className="fee-card overflow-hidden rounded-[22px] border border-amber-100 bg-white shadow-[0_10px_35px_rgba(80,25,25,0.055)]">

                    <div className="flex items-center gap-3 border-b border-amber-100 bg-gradient-to-r from-amber-50 to-white px-5 py-4">

                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                        <CalendarDays size={19} />
                      </div>

                      <h3 className="font-serif text-lg font-medium text-slate-900">
                        Payment Schedule
                      </h3>
                    </div>

                    <div className="divide-y divide-slate-100 px-5 sm:px-6">
                      {paymentSchedule.map((entry, index) => (
                        <div
                          key={index}
                          className="flex flex-col gap-1 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                        >
                          <span className="text-[13px] text-slate-600">
                            {entry.label}
                          </span>

                          <span className="text-[13px] font-medium text-red-800 sm:text-right">
                            {entry.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CONTACT INFORMATION */}
            {feeStructure.contactText && (
              <div className="fee-card mt-6 flex items-start gap-3 rounded-xl border-l-4 border-amber-400 bg-white px-5 py-4 shadow-sm">

                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-700">
                  <FileText size={18} />
                </div>

                <div>
                  <p className="text-sm font-medium text-slate-900">
                    Fee Enquiries
                  </p>

                  <p className="mt-1 whitespace-pre-line text-[13px] leading-7 text-slate-600">
                    {feeStructure.contactText}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* PRINT STYLES */}
      <style>
        {`
          @media print {
            @page {
              size: A4 landscape;
              margin: 12mm;
            }

            body {
              background: white !important;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            body * {
              visibility: hidden !important;
            }

            .fee-print-area,
            .fee-print-area * {
              visibility: visible !important;
            }

            .fee-print-area {
              position: absolute !important;
              top: 0 !important;
              left: 0 !important;
              width: 100% !important;
              max-width: none !important;
              margin: 0 !important;
              padding: 0 !important;
            }

            .fee-actions {
              display: none !important;
            }

            .fee-card {
              box-shadow: none !important;
              break-inside: avoid;
              margin-bottom: 14px !important;
            }

            .fee-table {
              min-width: 0 !important;
              width: 100% !important;
              font-size: 11px !important;
            }

            thead {
              display: table-header-group;
            }

            tr {
              break-inside: avoid;
            }
          }
        `}
      </style>
    </main>
  );
}

